import React, { useEffect } from "react";
import '../../styles/components/lesson/segment.scss';
import SoundsliceIframe from "./SoundsliceIframe";
import Keyboard from "../keyboard/Keyboard";
import {ProgressBar} from "./progressBar/ProgressBar";
import {FontIcon, Icon} from "../common/FontIcon";
import {setProgressBarVisibility} from "./progressBar/ProgressBarLogic";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {isBirdsEyeKeyboardVisible} from "../../models/view/lessonPage/LessonPlayerSettings";
import useScreenOrientation from "../common/hooks/useScreenOrientation";
import {logger} from "../../services/log/logger";
import {setHorizontalScrollAndVideoPositionBasedOnOrientation} from "../../redux/lesson/lessonActions";
import playbackOrchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";

export default function Segment() {
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const descriptionHtml = segment?.descriptionHtml;
  const lessonPlayerSettings = segment?.lessonPlayerSettings;
  const isKeyboardVisible = !!(lessonPlayerSettings && segment && isBirdsEyeKeyboardVisible(lessonPlayerSettings, segment.mode));
  const hasVideo = segment?.hasVideo;
  const shouldHideSegmentDescription = useAppSelector(lessonPlayerStore.getShouldHideSegmentDescription);

  const descriptionEl = createDescriptionEl(
    descriptionHtml,
    shouldHideSegmentDescription ?? false,
    (shouldHideSegmentDescription: boolean) => lessonPlayerStore.setShouldHideSegmentDescription(!shouldHideSegmentDescription)
  );
  const noKeyboardClassName = isKeyboardVisible ? "" : "no-keyboard";
  const keyboard = isKeyboardVisible ? <Keyboard/> : null;
  const videoProgressBar = hasVideo ? <ProgressBar/> : null;

  const screenOrientation = useScreenOrientation();
  useEffect(() => {
    logger.deviceScreen.info({
      message: `orientation changed so updating horizontal scroll and video position: `,
      data: [
        { key: "screenOrientation", value: screenOrientation }
      ]
    });

    // Need to keep screenOrientation as a dependency to ensure this effect runs when it changes.
    setHorizontalScrollAndVideoPositionBasedOnOrientation();
  }, [screenOrientation]);

  return (
    <div className={`segment ${noKeyboardClassName}`} onMouseOver={() => { setProgressBarVisibility(); }}>
      {descriptionEl}
      <SoundsliceIframe onLoad={onLoadIframe}/>
      {keyboard}
      {videoProgressBar}
    </div>
  );
}

function onLoadIframe() {
  // When SoundSlice iFrame is initialized, we can pass some parameters to configure it. However, some of the newer SoundSlice
  // settings are not yet supported via iFrame url parameters. Need to set those here in the onLoad callback.

  // Enable proportional notation?
  const isProportionSpacingEnabled = lessonPlayerStore.getIsProportionalSpacingEnabled();
  playbackOrchestrator.setIsProportionalNotationEnabled(isProportionSpacingEnabled);

  // Proportional spacing
  if (isProportionSpacingEnabled) {
    const notationSpacing = lessonPlayerStore.getSpacingForActiveLesson();
    playbackOrchestrator.setNotationSpacing(notationSpacing ?? lessonPlayerStore.getDefaultProportionalSpacing());
  }
}

/**
 * Conditionally show the description in collapsed or expanded form, based on whether the user has seen it before, and whether playback is occurring.
 */
function createDescriptionEl(descriptionHtml: string | undefined, shouldHideSegmentDescription: boolean, onToggleDescription: (shouldHideSegmentDescription: boolean) => void){
  let descriptionEl = null;
  if(descriptionHtml){
    const openCloseClassName = shouldHideSegmentDescription ? "" : "open";
    const iconClassName: Icon = shouldHideSegmentDescription ? "icon-angle-down" : "icon-angle-up";
    descriptionEl = <div className={`segment-description ${openCloseClassName}`}>
      <div className="description" dangerouslySetInnerHTML={ {__html: descriptionHtml } }/>

      <div className={`expand-collapse-icon`} onClick={()=> onToggleDescription(shouldHideSegmentDescription)}>
        <FontIcon icon={iconClassName}/>
      </div>
    </div>;
  }
  return descriptionEl
}
